import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrgProvider, useOrg } from './contexts/OrgContext';
import { HOME_ROUTE, homePathForRole } from './lib/homeRoute';
import { needsPlanCheckout } from './lib/billing';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import SetupAccount from './pages/SetupAccount';
import { loggedInDestination, hasPendingGoogleSignup } from './lib/googleAuth';
import Dashboard from './pages/Dashboard';
import CreationPanel from './pages/CreationPanel';
import TelaEmBranco from './pages/TelaEmBranco';
import Layout from './components/Layout';
import MediaGallery from './pages/MediaGallery';
import UserManagement from './pages/UserManagement';
import FinancialDashboard from './pages/FinancialDashboard';
import Payments from './pages/Payments';
import SubscribePlan from './pages/SubscribePlan';
import RequestManagement from './pages/RequestManagement';
import Marketplace from './pages/Marketplace';
import ReferralPanel from './pages/ReferralPanel';
import TagManagement from './pages/TagManagement';
import VideoEditor from './pages/VideoEditor';
import VirtualStaging from './pages/VirtualStaging';
import KanbanBoard from './pages/KanbanBoard';
import AccountSettings from './pages/AccountSettings';
import ExploreGallery from './pages/ExploreGallery';
import CreatorStudio from './pages/CreatorStudio';
import CreatorStudioEditor from './pages/CreatorStudioEditor';
import { MARKETPLACE_ENABLED } from './lib/featureFlags';
import { ShellSkeleton, AuthCardSkeleton } from './components/ui/AppBootSkeleton';
import AccountDisabled, { SignupLinkRequired } from './pages/AccountDisabled';
import './index.css';
import './ui.css';

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, needsSetup } = useAuth();
  const { org, role, loading: orgLoading } = useOrg();
  
  if (loading || (user && orgLoading)) {
    return <ShellSkeleton />;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Redirect invited users who haven't completed setup
  if (needsSetup) {
    return <Navigate to="/setup-account" />;
  }

  if (hasPendingGoogleSignup()) {
    return <Navigate to="/auth/callback" />;
  }

  if (!org) {
    return <SignupLinkRequired />;
  }

  if (org.disabled_at) {
    return <AccountDisabled message={org.disabled_message} />;
  }

  if (needsPlanCheckout(role, org)) {
    return <Navigate to="/assinar" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

const DisabledGate = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const { org, loading: orgLoading } = useOrg();
  if (loading || (user && orgLoading)) return <ShellSkeleton />;
  if (user && !org) return <SignupLinkRequired />;
  if (org?.disabled_at) return <AccountDisabled message={org.disabled_message} />;
  return <>{children}</>;
};

const HomeRedirect = () => {
  const { user, loading, needsSetup } = useAuth();
  const { org, role, loading: orgLoading } = useOrg();

  if (loading || (user && orgLoading)) {
    return <ShellSkeleton />;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (needsSetup) return <Navigate to="/setup-account" replace />;
  if (hasPendingGoogleSignup()) return <Navigate to="/auth/callback" replace />;
  if (!org) return <SignupLinkRequired />;
  if (org.disabled_at) return <AccountDisabled message={org.disabled_message} />;
  if (needsPlanCheckout(role, org)) return <Navigate to="/assinar" replace />;
  return <Navigate to={homePathForRole(role)} replace />;
};

function AppRoutes() {
  const { user, loading, needsSetup, passwordRecovery } = useAuth();

  if (loading) return <AuthCardSkeleton />;

  if (passwordRecovery) {
    return (
      <Routes>
        <Route path="/reset-password" element={<div className="auth-container"><ResetPassword /></div>} />
        <Route path="*" element={<Navigate to="/reset-password" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to={user ? loggedInDestination(needsSetup) : '/login'} replace />} />

      <Route path="/login" element={user ? <Navigate to={loggedInDestination(needsSetup)} /> : <div className="auth-container"><Login /></div>} />
      <Route path="/register/:token" element={user ? <Navigate to={loggedInDestination(needsSetup)} /> : <div className="auth-container"><Register /></div>} />
      <Route path="/register" element={user ? <Navigate to={loggedInDestination(needsSetup)} /> : <div className="auth-container"><Register /></div>} />
      <Route path="/auth/callback" element={<div className="auth-container"><AuthCallback /></div>} />
      <Route path="/reset-password" element={<div className="auth-container"><ResetPassword /></div>} />
      
      {/* Setup page for invited users */}
      <Route path="/setup-account" element={
        !user ? <Navigate to="/login" /> :
        !needsSetup ? <Navigate to={HOME_ROUTE} /> :
        <SetupAccount />
      } />

      <Route path="/assinar" element={
        !user ? <Navigate to="/login" /> :
        needsSetup ? <Navigate to="/setup-account" /> :
        hasPendingGoogleSignup() ? <Navigate to="/auth/callback" /> :
        <DisabledGate><div className="auth-container"><SubscribePlan /></div></DisabledGate>
      } />
      
      <Route path={HOME_ROUTE} element={<HomeRedirect />} />

      {/* Protected Routes inside Layout */}
      <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/explorar" element={<ProtectedRoute><ExploreGallery /></ProtectedRoute>} />
      <Route path="/estudio" element={<ProtectedRoute><CreatorStudio /></ProtectedRoute>} />
      <Route path="/estudio/:templateId" element={<ProtectedRoute><CreatorStudioEditor /></ProtectedRoute>} />
      
      <Route path="/gerenciamento/usuarios" element={
        <ProtectedRoute><UserManagement /></ProtectedRoute>
      } />
      
      <Route path="/financeiro" element={
        <ProtectedRoute><FinancialDashboard /></ProtectedRoute>
      } />
      
      <Route path="/pagamento" element={
        <ProtectedRoute><Payments /></ProtectedRoute>
      } />
      
      <Route path="/solicitacoes" element={
        <ProtectedRoute><RequestManagement /></ProtectedRoute>
      } />
      
      <Route path="/painel-criacao" element={
        <ProtectedRoute><CreationPanel /></ProtectedRoute>
      } />

      <Route path="/tela-em-branco" element={
        <ProtectedRoute><TelaEmBranco /></ProtectedRoute>
      } />
      <Route path="/conta" element={
        <ProtectedRoute><AccountSettings /></ProtectedRoute>
      } />
      <Route path="/fragmentador" element={<Navigate to="/tela-em-branco" replace />} />
      
      <Route path="/galeria" element={
        <ProtectedRoute><MediaGallery /></ProtectedRoute>
      } />

      <Route path="/kanban" element={
        <ProtectedRoute><KanbanBoard /></ProtectedRoute>
      } />
      
      {MARKETPLACE_ENABLED ? (
        <Route path="/marketplace" element={
          <ProtectedRoute><Marketplace /></ProtectedRoute>
        } />
      ) : (
        <Route path="/marketplace" element={<Navigate to={HOME_ROUTE} replace />} />
      )}
      
      <Route path="/indicacoes" element={
        <ProtectedRoute><ReferralPanel /></ProtectedRoute>
      } />
      
      <Route path="/tags" element={
        <ProtectedRoute><TagManagement /></ProtectedRoute>
      } />
      
      <Route path="/editor" element={
        <ProtectedRoute><VideoEditor /></ProtectedRoute>
      } />
      
      <Route path="/mobiliacao" element={
        <ProtectedRoute><VirtualStaging /></ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to={user ? loggedInDestination(needsSetup) : '/login'} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <OrgProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </OrgProvider>
    </AuthProvider>
  );
}

export default App;
